import { useState } from 'react';
import { ArrowLeft, Target, Clock, RefreshCcw, Flame, ChevronDown, ChevronUp } from 'lucide-react';
import { useQuiz } from '../context/QuizContext';
import { useGameAudio } from '../hooks/useGameAudio';

export function EstatisticasScreen() {
    const { state, dispatch } = useQuiz();
    const { playHover, playClick } = useGameAudio();
    const metricas = Object.entries(state.historicoMetricas);

    const autoExpandPhase = metricas.length > 0 ? metricas[metricas.length - 1][0] : null;
    const [fasesExpandidas, setFasesExpandidas] = useState<Record<string, boolean>>({
        ...(autoExpandPhase ? { [autoExpandPhase]: true } : {})
    });

    const toggleExpand = (faseStr: string) => {
        playClick();
        setFasesExpandidas(prev => ({
            ...prev,
            [faseStr]: !prev[faseStr]
        }));
    };

    return (
        <div className="h-full max-h-[100dvh] flex flex-col p-3 sm:p-4 mx-auto w-full max-w-lg animate-in fade-in duration-300 relative z-10 overflow-hidden justify-between">

            {/* Header Compacto */}
            <div className="glass-panel p-2.5 px-3 sm:px-4 rounded-xl mb-3 border-sky-500/30 flex items-center justify-between gap-3 bg-slate-900/80 shadow-neon-cyan shrink-0">
                <div className="flex items-center gap-3">
                    <button
                        onMouseEnter={playHover}
                        onClick={() => { playClick(); dispatch({ type: 'NAVIGATE', payload: 'MENU' }); }}
                        className="p-2 rounded-lg bg-slate-800 text-sky-400 hover:bg-sky-500 hover:text-white transition border border-slate-700 hover:border-sky-400 shrink-0"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <h1 className="font-cyber font-black tracking-wider text-sm sm:text-base uppercase text-sky-400 flex items-center gap-2 drop-shadow-md">
                            <Target size={18} />
                            Desempenho
                        </h1>
                    </div>
                </div>
            </div>

            {/* Conteúdo com Scroll Interno com Fading de Borda (Opção B) */}
            {metricas.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-6 glass-panel rounded-xl border border-sky-500/30 my-auto">
                    <div className="w-16 h-16 rounded-full border-2 border-dashed border-sky-500/50 flex items-center justify-center mb-4 animate-[rotate_10s_linear_infinite]">
                        <Target size={28} className="text-sky-400 opacity-50" />
                    </div>
                    <h2 className="text-lg font-cyber font-bold text-white mb-2 tracking-wider uppercase">Sem Registros Acásicos</h2>
                    <p className="text-slate-400 text-xs max-w-xs">
                        Jogue as fases para que os Deuses registrem o seu histórico de sabedoria e sobrevivência.
                    </p>
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto custom-scrollbar scroll-edge-fade pr-1 space-y-3 pb-2 min-h-0">
                    {metricas.map(([faseStr, dados]) => {
                        const totalPerguntasEsperadas = 16 * dados.jogadas;
                        const precisao = totalPerguntasEsperadas > 0 ? Math.round((dados.acertos / totalPerguntasEsperadas) * 100) : 0;
                        const totalRespostas = dados.acertos + dados.erros;
                        const tempoMedio = totalRespostas > 0 ? (dados.tempoTotalGasto / totalRespostas).toFixed(1) : 0;
                        const logPerguntas = Object.values(dados.historicoPerguntas);
                        const isExpanded = !!fasesExpandidas[faseStr];

                        return (
                            <div key={faseStr} className="glass-panel rounded-xl border border-sky-900/60 overflow-hidden transition-all duration-300">
                                <div
                                    onMouseEnter={playHover}
                                    className="bg-slate-900/90 p-3 border-b border-sky-900/50 flex justify-between items-center cursor-pointer hover:bg-slate-800 transition-colors"
                                    onClick={() => toggleExpand(faseStr)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center justify-center w-8 h-8 bg-slate-900 border border-sky-500/50 text-sky-400 font-cyber font-bold text-xs rounded-lg">
                                            {faseStr}
                                        </div>
                                        <div className="flex gap-1.5 text-[10px] font-cyber font-bold uppercase tracking-wider">
                                            <span className="bg-emerald-900/40 text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 rounded">
                                                Vit. {dados.vitorias}
                                            </span>
                                            <span className="bg-red-900/40 text-red-400 border border-red-500/30 px-1.5 py-0.5 rounded">
                                                Der. {dados.derrotas}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-sky-400/50">
                                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="p-3 sm:p-4 bg-slate-900/40 animate-in fade-in duration-200">
                                        <div className="grid grid-cols-2 gap-2 mb-3">
                                            <div className="glass-panel p-2 border-sky-500/20 flex flex-col items-center text-center">
                                                <Target size={16} className="text-sky-400 mb-0.5" />
                                                <span className="text-lg font-cyber font-bold text-white">{precisao}%</span>
                                                <span className="text-[9px] text-slate-400 font-cyber tracking-widest uppercase">Precisão</span>
                                            </div>
                                            <div className="glass-panel p-2 border-amber-500/20 flex flex-col items-center text-center">
                                                <Clock size={16} className="text-amber-400 mb-0.5" />
                                                <span className="text-lg font-cyber font-bold text-white">{tempoMedio}s</span>
                                                <span className="text-[9px] text-slate-400 font-cyber tracking-widest uppercase">Tempo Médio</span>
                                            </div>
                                            <div className="glass-panel p-2 border-orange-500/20 flex flex-col items-center text-center">
                                                <Flame size={16} className="text-orange-500 mb-0.5" />
                                                <span className="text-lg font-cyber font-bold text-white">{dados.maiorCombo}</span>
                                                <span className="text-[9px] text-slate-400 font-cyber tracking-widest uppercase">Maior Combo</span>
                                            </div>
                                            <div className="glass-panel p-2 border-purple-500/20 flex flex-col items-center text-center">
                                                <RefreshCcw size={16} className="text-purple-400 mb-0.5" />
                                                <span className="text-lg font-cyber font-bold text-white">{dados.jogadas}</span>
                                                <span className="text-[9px] text-slate-400 font-cyber tracking-widest uppercase">Jogadas</span>
                                            </div>
                                        </div>

                                        {logPerguntas.length > 0 && (
                                            <div className="mt-3">
                                                <p className="text-[9px] text-sky-400 font-cyber font-bold uppercase tracking-widest mb-2">
                                                    Últimos Registros
                                                    {logPerguntas.length > 5 && (
                                                        <span className="ml-2 text-slate-500 normal-case tracking-normal">
                                                            (5 de {logPerguntas.length})
                                                        </span>
                                                    )}
                                                </p>
                                                {/* Sem scroll aninhado (era um 2º nível de rolagem dentro da
                                                    lista rolável — desorientador em touch): só os 5 últimos. */}
                                                <div className="space-y-1.5">
                                                    {logPerguntas.slice(-5).map((log) => (
                                                        <div key={log.id} className="flex gap-2 items-center p-2 bg-slate-900/60 rounded-lg border border-slate-700/50">
                                                            <div className={`shrink-0 flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold
                                                                ${log.status === 'acerto' ? 'bg-emerald-900/40 border border-emerald-500/50 text-emerald-400' : 'bg-red-900/40 border border-red-500/50 text-red-500'}`}>
                                                                {log.status === 'acerto' ? 'V' : 'X'}
                                                            </div>
                                                            <span className="text-xs truncate font-medium text-slate-300">
                                                                {log.texto}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <button
                onMouseEnter={playHover}
                onClick={() => { playClick(); dispatch({ type: 'NAVIGATE', payload: 'MENU' }); }}
                className="btn-cyber w-full py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 font-cyber font-bold tracking-wider hover:text-white hover:bg-slate-700 transition uppercase text-xs shrink-0 mt-2"
            >
                Voltar ao Menu Principal
            </button>
        </div>
    );
}
